import { useQuery } from '@tanstack/react-query'
import { Shirt, TriangleAlert } from 'lucide-react'
import {
  Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchLaundry, formatDateTime, formatQuantity, nameIn, pluralise,
  translateError, type LaundryRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   W-103 — WHAT IS AT THE LAUNDRY.  Sprint 68.

   ── THE BALANCE IS NOT THE QUESTION ────────────────────────────────────────────
   W-100 already says the laundry holds sixty bath towels. That is true and it is not what
   anybody needs to know: **sixty that went down this morning and sixty that went down on
   Saturday are the same number and opposite situations**, and only the second is a
   telephone call. Nothing in this product could tell them apart until this screen.

   ── WHAT MAKES IT COMPUTABLE ───────────────────────────────────────────────────
   Linen is not identified — a towel is a towel — so which physical pieces came back is
   unknowable. What is knowable is the AGE OF THE BALANCE, on the assumption every laundry
   actually follows: what went down first comes back first. `LaundryQueue` holds that rule
   and says out loud that it can be wrong, and in which direction.

   ── AND THE RHYTHM, BECAUSE IT COMES BEFORE THE BALANCE ────────────────────────
   Sent and returned over the last week sit beside the age. A laundry sending back half of
   what it takes looks fine on a balance for several days and is already failing; the two
   columns show it a week earlier than the shelf does.
   ========================================================================= */

export function Laundry({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const board = useQuery({
    queryKey: ['laundry', propertyId],
    queryFn: ({ signal }) => fetchLaundry(propertyId, { signal }),
    /* Five minutes. Linen moves in loads, not in seconds, and a screen somebody keeps open
       to telephone about should not be redrawing itself under them. */
    refetchInterval: 5 * 60_000,
    retry: false,
  })

  if (board.isLoading && !board.error) return <LoadingState rows={5} />

  if (board.error && !board.data) {
    if (board.error instanceof ApiError && board.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(board.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={board.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = board.data!
  const anythingThere = view.rows.some((r) => r.atLaundry > 0)

  return (
    <div className="space-y-4 p-4">
      {/* **THE SENTENCE FIRST, AND IT NAMES THE TURNAROUND.** A reader who does not know
          what the hotel's laundry has promised cannot judge a number of hours — and the
          promise comes from the server, so this sentence and the rule that decides what is
          late cannot be two different numbers. */}
      <p className="text-sm">
        {view.overdue === 0
          ? fill(t.lnAllOnTime, {
            hours: formatQuantity(view.turnaroundHours, locale),
          })
          : fill(
            pluralise(t.lnOverdue, view.overdue, locale,
              formatQuantity(view.overdue, locale)),
            { hours: formatQuantity(view.turnaroundHours, locale) })}
      </p>

      <Card>
        <CardHeader
          title={view.laundryName
            ? nameIn(locale, view.laundryName, view.laundryNameAr)
            : t.lnTitle}
          icon={<Shirt className="size-4" />}
        />
        {!view.laundryLocationId ? (
          <CardBody>
            {/* A hotel that sends its linen out and has not said where. A setting nobody
                has made, not a fault — the answer W-135 gives about everything else. */}
            <EmptyState title={t.lnNoLaundry} />
          </CardBody>
        ) : !anythingThere ? (
          <CardBody><EmptyState title={t.lnNothingThere} /></CardBody>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.stockItem}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.lnThere}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.lnOut}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.lnSent}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.lnBack}</th>
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row) => <Row key={row.stockItemId} row={row} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-[12px] text-muted-foreground">{t.lnNote}</p>
    </div>
  )
}

function Row({ row }: { row: LaundryRow }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  return (
    <tr className={`border-b border-border/60 last:border-0 ${
      row.isOverdue ? 'bg-[#fffbeb] dark:bg-[#2a1e08]' : ''}`}
    >
      <td className="px-3 py-2">
        <bdi className="text-foreground">{nameIn(locale, row.itemName, row.itemNameAr)}</bdi>
        {row.isOverdue && (
          <span className="ms-2 inline-flex items-center gap-1 text-[11.5px] font-medium"
                style={{ color: 'var(--hk-hk-dirty-text)' }}>
            <TriangleAlert className="size-3" aria-hidden />
            {t.lnLate}
          </span>
        )}
      </td>

      <td className="px-3 py-2 text-end tnum font-medium">
        {formatQuantity(row.atLaundry, locale)}
      </td>

      {/* **AN EM DASH WHERE NOTHING IS THERE**, never "0h". A zero would say something went
          down this instant, which is a different claim from nothing being out at all. */}
      <td className={`px-3 py-2 text-end tnum ${
        row.isOverdue ? 'font-semibold text-[#b45309] dark:text-[#fbbf24]'
          : 'text-muted-foreground'}`}
      >
        {row.hoursOut === null
          ? '—'
          : fill(t.lnHours, { hours: formatQuantity(row.hoursOut, locale) })}
        {row.oldestSentAtUtc && (
          <span className="block text-[11px] font-normal text-muted-foreground">
            {formatDateTime(new Date(row.oldestSentAtUtc), locale)}
          </span>
        )}
      </td>

      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatQuantity(row.sentRecently, locale)}
      </td>
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatQuantity(row.returnedRecently, locale)}
      </td>
    </tr>
  )
}
